import { useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { Logomark } from '../../../atoms/Logomark/Logomark';
import { Text } from '../../../atoms/Text/Text';
import { ChatListItem } from '../../../molecules/ChatListItem/ChatListItem';
import { InputField } from '../../../molecules/InputField/InputField';
import { MessageActions } from '../../../molecules/MessageActions/MessageActions';
import { Modal } from '../../../molecules/Modal/Modal';
import type { ModelOption } from '../../../molecules/ModelSelector/ModelSelector';
import { PromptComposer } from '../../../molecules/PromptComposer/PromptComposer';
import {
  ReasoningTrace,
  type ReasoningStep,
} from '../../../molecules/ReasoningTrace/ReasoningTrace';
import { Search } from '../../../molecules/Search/Search';
import { Select, type SelectOption } from '../../../molecules/Select/Select';
import {
  StreamingText,
  type AnswerFigures,
  type AnswerRecommendation,
  type Citation,
  type SuggestedPrompt,
} from '../../../molecules/StreamingText/StreamingText';
import { withoutFigureMarkers } from '../../../molecules/StreamingText/answerFormat';
import { SuggestionPill } from '../../../molecules/SuggestionPill/SuggestionPill';
import styles from './DeepDiveWorkspace.module.css';

export const DEEPDIVE_MODES: ModelOption[] = [
  {
    id: 'fast',
    label: 'Fast',
    description: 'Direct lookups - counts, breakdowns, rankings',
    icon: <Icon name="fast" />,
  },
  {
    id: 'expert',
    label: 'Expert',
    description: 'Breaks the question into steps for diagnosis',
    icon: <Icon name="aiSpark" />,
  },
];

export type ChatHistoryItem = { id: string; title: string; timeLabel: string; starred: boolean };
export type DeepDiveMessage =
  | { id: string; role: 'user'; text: string }
  | {
      id: string;
      role: 'assistant';
      status: 'thinking' | 'streaming' | 'done' | 'stopped';
      text: string;
      reasoning: ReasoningStep[];
      citations: Citation[];
      followUps: SuggestedPrompt[];
      feedback: 'positive' | 'negative' | null;
      chart?: ReactNode;
      figures?: AnswerFigures;
      /** Figures the analyst placed, by the id of their `[[figure:<id>]]` line in `text`. */
      figureNodes?: Record<string, ReactNode>;
      notes?: string[];
      recommendations?: AnswerRecommendation[];
      limitations?: string[];
      /** Offer to ask the same question again in Expert mode (the answer came from Fast). */
      offerExpert?: boolean;
    };

export function DeepDiveLanding({
  mode,
  suggestions,
  pending,
  onModeChange,
  onSend,
}: {
  mode: string;
  suggestions: string[];
  pending?: boolean;
  onModeChange: (mode: string) => void;
  onSend: (text: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const field = useRef<HTMLTextAreaElement>(null);
  return (
    <div className={styles.emptyPage}>
      <div className={styles.intro}>
        <Logomark className={styles.mark} label="" />
        <h2 className={styles.heading}>Ask anything about your data</h2>
      </div>
      <div className={styles.composerWrap}>
        <div className={styles.halo} aria-hidden />
        <PromptComposer
          className={styles.composer}
          modes={DEEPDIVE_MODES}
          mode={mode}
          onModeChange={onModeChange}
          value={draft}
          onValueChange={setDraft}
          inputRef={field}
          status={pending ? 'generating' : 'idle'}
          onSend={onSend}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- this is the page's primary task and opens after explicit navigation.
          autoFocus
        />
      </div>
      <div className={styles.suggestions}>
        <p className={styles.suggestionsLabel}>Suggested prompts for you</p>
        <ul className={styles.pills} aria-label="Suggested prompts">
          {suggestions.map((prompt) => (
            <li key={prompt}>
              <SuggestionPill
                label={prompt}
                onSelect={(text) => {
                  setDraft(text);
                  field.current?.focus();
                }}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const SORT_OPTIONS: SelectOption[] = [
  { value: 'newest', label: 'Sort by', supportingText: 'Newest' },
  { value: 'oldest', label: 'Sort by', supportingText: 'Oldest' },
];
const ROW_MENU = [
  { id: 'rename', label: 'Rename' },
  { id: 'delete', label: 'Delete' },
];
export function ChatHistoryWorkspace({
  chats,
  query,
  order,
  onQueryChange,
  onOrderChange,
  onRename,
  onDelete,
  onStar,
}: {
  chats: ChatHistoryItem[];
  query: string;
  order: 'newest' | 'oldest';
  onQueryChange: (value: string) => void;
  onOrderChange: (value: 'newest' | 'oldest') => void;
  onRename: (chat: ChatHistoryItem) => void;
  onDelete: (id: string) => void;
  onStar: (id: string, starred: boolean) => void;
}) {
  return (
    <div className={styles.history}>
      <div className={styles.filtersRow}>
        <Text as="h2" size="lg" weight="medium" color="primary" className={styles.historyTitle}>
          Chat history
        </Text>
        <div className={styles.actionsRow}>
          <div className={styles.search}>
            <Search
              value={query}
              onValueChange={onQueryChange}
              placeholder="Search chats..."
              aria-label="Search chats"
            />
          </div>
          <div className={styles.sort}>
            <Select
              aria-label="Sort by"
              options={SORT_OPTIONS}
              value={order}
              onValueChange={(value) => onOrderChange(value === 'oldest' ? 'oldest' : 'newest')}
            />
          </div>
          <Button hierarchy="secondary" href="/deepdive-ai" iconTrailing={<Icon name="add" />}>
            New chat
          </Button>
        </div>
      </div>
      <ul className={styles.chatList} aria-label="Chats">
        {chats.map((chat) => (
          <ChatListItem
            key={chat.id}
            title={chat.title}
            timeLabel={chat.timeLabel}
            href={`/deepdive-ai/${chat.id}`}
            menuItems={ROW_MENU}
            onMenuSelect={(id) => (id === 'rename' ? onRename(chat) : onDelete(chat.id))}
            starred={chat.starred}
            onStarredChange={(starred) => onStar(chat.id, starred)}
          />
        ))}
      </ul>
      {chats.length === 0 && (
        <p className={styles.noMatches}>
          <Text size="sm" color="tertiary">
            No chats match your search.
          </Text>
        </p>
      )}
    </div>
  );
}

export function RenameChatModal({
  chat,
  onClose,
  onConfirm,
}: {
  chat: ChatHistoryItem | null;
  onClose: () => void;
  onConfirm: (title: string) => void;
}) {
  const [title, setTitle] = useState(chat?.title ?? '');
  const trimmed = title.trim();
  const confirm = () => {
    if (trimmed) onConfirm(trimmed);
  };
  return (
    <Modal
      open={chat !== null}
      onClose={onClose}
      title="Rename chat"
      icon={<Icon name="edit" />}
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      actionsLayout="fill"
      onConfirm={confirm}
      confirmDisabled={!trimmed}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          confirm();
        }}
      >
        <InputField
          aria-label="Chat name"
          value={title}
          onValueChange={setTitle}
          placeholder="Chat name"
        />
      </form>
    </Modal>
  );
}

export function ConversationWorkspace({
  messages,
  mode,
  generating,
  onModeChange,
  onSend,
  onStop,
  onFollowUp,
  onRate,
  onRegenerate,
  onAskExpert,
}: {
  messages: DeepDiveMessage[];
  mode: string;
  generating: boolean;
  onModeChange: (mode: string) => void;
  onSend: (text: string) => void;
  onStop: () => void;
  onFollowUp: (text: string) => void;
  onRate: (id: string, feedback: 'positive' | 'negative' | null) => void;
  onRegenerate: (id: string) => void;
  /** Asks the answer's question again in Expert mode, as a new turn. */
  onAskExpert?: (id: string) => void;
}) {
  const last = messages.at(-1);
  const answered = last?.role === 'assistant' && last.status === 'done';
  return (
    <div className={styles.chatPage}>
      <ol className={styles.messages} aria-label="Conversation">
        {messages.map((message) =>
          message.role === 'user' ? (
            <li key={message.id} className={styles.user}>
              <span className={styles.userLabel}>You</span>
              <p className={styles.userText}>{message.text}</p>
            </li>
          ) : message.status === 'thinking' ? (
            <li key={message.id} className={styles.assistant}>
              <ReasoningTrace steps={message.reasoning} status="thinking" defaultOpen fill />
            </li>
          ) : (
            <li key={message.id} className={styles.assistant} data-message>
              <StreamingText
                fill
                text={message.text}
                streaming={message.status === 'streaming'}
                citations={message.status === 'done' ? message.citations : []}
                followUps={message.status === 'done' ? message.followUps : []}
                onFollowUp={(prompt) => onFollowUp(prompt.prompt ?? prompt.label)}
                chart={message.status === 'done' ? message.chart : undefined}
                figures={message.status === 'done' ? message.figures : undefined}
                figureNodes={message.status === 'done' ? message.figureNodes : undefined}
                notes={message.status === 'done' ? message.notes : undefined}
                recommendations={message.status === 'done' ? message.recommendations : undefined}
                limitations={message.status === 'done' ? message.limitations : undefined}
                actions={
                  <MessageActions
                    revealOnHover
                    content={withoutFigureMarkers(message.text)
                      .replace(/\*\*/g, '')
                      .replace(/\s\[\d+\]/g, '')}
                    feedback={message.feedback}
                    onFeedback={(feedback) => onRate(message.id, feedback)}
                    onRegenerate={() => onRegenerate(message.id)}
                    onAskExpert={
                      message.status === 'done' && message.offerExpert && onAskExpert
                        ? () => onAskExpert(message.id)
                        : undefined
                    }
                  />
                }
              />
            </li>
          ),
        )}
      </ol>
      <div className={styles.dock}>
        <div className={styles.composerWrap}>
          <PromptComposer
            className={styles.composer}
            modes={DEEPDIVE_MODES}
            mode={mode}
            onModeChange={onModeChange}
            status={generating ? 'generating' : answered ? 'answered' : 'idle'}
            onSend={onSend}
            onStop={onStop}
          />
        </div>
      </div>
      <div className={styles.fadeAnchor} aria-hidden>
        <div className={styles.fade} />
      </div>
      <div className={styles.glowAnchor} aria-hidden>
        <div className={styles.halo} data-generating={generating || undefined} />
      </div>
    </div>
  );
}
